import { formatNumberFundingRate } from "util/format"

import { Box, BoxProps } from "@chakra-ui/react"
import Big from "big.js"
import { Stats } from "component/Stats"
import useDimensions from "react-cool-dimensions"

interface MouseOverProps {
    mouseX: number
    mouseY: number
    chartWidth: number
    chartHeight: number
    priceScaleWidth: number
    timescaleHeight: number
}

export interface TooltipProps extends BoxProps {
    data?: {
        fundingRate: number
        timeStr?: string
    }
    mouseOverProps?: MouseOverProps
}

const OFFSET_X_BOUNDARY = 24
const OFFSET_Y_BOUNDARY = 48
const OFFSET_Y_MOUSE = 24

const getCardMouseOverPositionProps = ({
    mouseOverProps,
    cardWidth,
    cardHeight,
}: {
    mouseOverProps?: MouseOverProps
    cardWidth: number
    cardHeight: number
}): BoxProps | undefined => {
    if (!mouseOverProps) {
        return
    }
    const topTarget = mouseOverProps.mouseY - cardHeight - OFFSET_Y_MOUSE
    const topMin = OFFSET_Y_BOUNDARY
    const top = Math.max(topTarget, topMin)

    const leftTarget = mouseOverProps.mouseX + mouseOverProps.priceScaleWidth - cardWidth / 2
    const leftMax = mouseOverProps.chartWidth - cardWidth - OFFSET_X_BOUNDARY
    const leftMin = mouseOverProps.priceScaleWidth + OFFSET_X_BOUNDARY
    const left = Math.max(Math.min(leftTarget, leftMax), leftMin)

    return { position: "absolute", top: `${top}px`, left: `${left}px` }
}

export const TooltipOverlay = ({ data, mouseOverProps, ...props }: TooltipProps) => {
    const { observe: observeCard, width: cardWidth, height: cardHeight } = useDimensions({ useBorderBoxSize: true })

    if (!data) {
        return null
    }
    const { fundingRate, timeStr } = data

    const containerProps: BoxProps = {
        position: "absolute",
        pointerEvents: "none",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
    }

    const cardProps: BoxProps = {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        overflow: "hidden",
        zIndex: 99,
        boxShadow: "lg",
        padding: 4,
        borderRadius: 8,
        bg: "blackAlpha.900",
        ...getCardMouseOverPositionProps({ mouseOverProps, cardWidth, cardHeight }),
    }

    return mouseOverProps ? (
        <Box {...containerProps}>
            <Box ref={observeCard} {...props} {...cardProps}>
                <Stats label={timeStr} number={formatNumberFundingRate(new Big(fundingRate))} />
            </Box>
        </Box>
    ) : null
}
